// Check-in Service - User presence at places

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  Unsubscribe,
  Timestamp,
} from '@firebase/firestore';
import { db } from './firebase';

const CHECKINS_COLLECTION = 'checkins';
const DEFAULT_CHECKIN_HOURS = 3; // Check-in expires after 3 hours

export interface CheckIn {
  id: string;
  placeId: string;
  userId: string;
  userName: string;
  userPhoto?: string;
  message?: string;
  checkedInAt: Timestamp | Date;
  expiresAt: Timestamp | Date;
}

/**
 * Check in to a place
 */
export async function checkIn(
  placeId: string,
  userId: string,
  userName: string,
  userPhoto?: string,
  message?: string
): Promise<string> {
  // First, check out from any existing check-in
  await checkOutAll(userId);

  // Calculate expiry time (3 hours from now)
  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getHours() + DEFAULT_CHECKIN_HOURS);

  // Build data object, only include optional fields if they have values
  const checkinData: Record<string, unknown> = {
    placeId,
    userId,
    userName,
    checkedInAt: serverTimestamp(),
    expiresAt: Timestamp.fromDate(expiresAt),
  };

  // Only add optional fields if they have values (Firestore doesn't accept undefined)
  if (userPhoto) checkinData.userPhoto = userPhoto;
  if (message) checkinData.message = message;

  const docRef = await addDoc(collection(db, CHECKINS_COLLECTION), checkinData);

  return docRef.id;
}

/**
 * Check out from a specific check-in
 */
export async function checkOut(checkinId: string): Promise<void> {
  await deleteDoc(doc(db, CHECKINS_COLLECTION, checkinId));
}

/**
 * Check out from all places (user can only be at one place)
 */
export async function checkOutAll(userId: string): Promise<void> {
  const q = query(
    collection(db, CHECKINS_COLLECTION),
    where('userId', '==', userId)
  );

  const snapshot = await getDocs(q);
  const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));
  await Promise.all(deletePromises);
}

/**
 * Get active check-ins for a place
 */
export async function getActiveCheckins(placeId: string): Promise<CheckIn[]> {
  const now = Timestamp.now();

  const q = query(
    collection(db, CHECKINS_COLLECTION),
    where('placeId', '==', placeId),
    where('expiresAt', '>', now),
    orderBy('expiresAt', 'asc'),
    orderBy('checkedInAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as CheckIn[];
}

/**
 * Get user's current active check-in
 */
export async function getUserActiveCheckin(userId: string): Promise<CheckIn | null> {
  const now = Timestamp.now();

  const q = query(
    collection(db, CHECKINS_COLLECTION),
    where('userId', '==', userId),
    where('expiresAt', '>', now),
    limit(1)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  return { id: doc.id, ...doc.data() } as CheckIn;
}

/**
 * Subscribe to active check-ins for a place (real-time)
 */
export function subscribeToPlaceCheckins(
  placeId: string,
  callback: (checkins: CheckIn[]) => void
): Unsubscribe {
  const now = Timestamp.now();

  const q = query(
    collection(db, CHECKINS_COLLECTION),
    where('placeId', '==', placeId),
    where('expiresAt', '>', now),
    orderBy('expiresAt', 'asc'),
    orderBy('checkedInAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    // Filter again in case of stale data
    const currentTime = new Date();
    const checkins = snapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }) as CheckIn)
      .filter((checkin) => {
        const expiresAt = checkin.expiresAt instanceof Timestamp
          ? checkin.expiresAt.toDate()
          : new Date(checkin.expiresAt);
        return expiresAt > currentTime;
      });
    callback(checkins);
  });
}

/**
 * Subscribe to user's active check-in (real-time)
 */
export function subscribeToUserCheckin(
  userId: string,
  callback: (checkin: CheckIn | null) => void
): Unsubscribe {
  const q = query(
    collection(db, CHECKINS_COLLECTION),
    where('userId', '==', userId),
    limit(1)
  );

  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
      return;
    }

    const doc = snapshot.docs[0];
    const checkin = { id: doc.id, ...doc.data() } as CheckIn;

    // Check if expired
    const expiresAt = checkin.expiresAt instanceof Timestamp
      ? checkin.expiresAt.toDate()
      : new Date(checkin.expiresAt);

    if (expiresAt <= new Date()) {
      callback(null);
    } else {
      callback(checkin);
    }
  });
}

/**
 * Get check-in count for a place (for badges)
 */
export async function getCheckinCount(placeId: string): Promise<number> {
  const now = Timestamp.now();

  const q = query(
    collection(db, CHECKINS_COLLECTION),
    where('placeId', '==', placeId),
    where('expiresAt', '>', now)
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Get check-in counts for multiple places (batch)
 */
export async function getCheckinCounts(placeIds: string[]): Promise<Map<string, number>> {
  const counts = new Map<string, number>();

  // Initialize all to 0
  placeIds.forEach((id) => counts.set(id, 0));

  if (placeIds.length === 0) return counts;

  const now = Timestamp.now();

  // Firestore 'in' query supports max 10 items, so we batch
  const batches = [];
  for (let i = 0; i < placeIds.length; i += 10) {
    const batch = placeIds.slice(i, i + 10);
    batches.push(batch);
  }

  for (const batch of batches) {
    const q = query(
      collection(db, CHECKINS_COLLECTION),
      where('placeId', 'in', batch),
      where('expiresAt', '>', now)
    );

    const snapshot = await getDocs(q);
    snapshot.docs.forEach((doc) => {
      const placeId = doc.data().placeId;
      counts.set(placeId, (counts.get(placeId) || 0) + 1);
    });
  }

  return counts;
}

/**
 * Format remaining time until check-in expires
 */
export function formatTimeRemaining(expiresAt: Timestamp | Date): string {
  const expiry = expiresAt instanceof Timestamp ? expiresAt.toDate() : new Date(expiresAt);
  const now = new Date();
  const diff = expiry.getTime() - now.getTime();

  if (diff <= 0) return 'Abgelaufen';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `Noch ${hours}h ${minutes}m`;
  }
  return `Noch ${minutes}m`;
}
